<template>
  <div class="">

    <div class="label-line flex flex-wrap">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputName 
          label="Full name of trust" 
          propertyKey="name"
          objectType="trust"
          :value="applicant.trust.name"
          :updateValue="updateValue"
          :onKeyup="updateTrustName"
          :required="true"></InputName>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputABN label="ABN of Trust (if applicable)" 
          propertyKey="abn"
          :value="applicant.trust.abn"
          objectType="trust"
          :updateValue="updateValue"></InputABN>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <div class="label-standard">Type of trust</div>

        <div class="form-line">
          <select 
            :value="applicant.trust.type == null ? -1 : applicant.trust.type"
            v-on:change="updateValue({
              objectType: 'trust',
              key       : 'type', 
              value     : $event.target.value
            })"
            class="select-standard">
            <option value="-1" disabled="">Select an option...</option>
            <option value="0">Individual/Family</option>
            <option value="1">Regulated Trust (SMSF)</option>
            <option value="2">Registered Managed Investment Scheme</option>
            <option value="3">Government Super Fund</option>
            <option value="4">Other</option>
          </select>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputText 
          label="Full Business Name (if any) of the Trustee in respect to the Trust" 
          propertyKey="business"
          objectType="trust"
          :value="applicant.trust.business"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 flex flex-wrap">
        <div class="w-1/3 pr-2">
          <BuildingTypeSelector
            propertyKey="building_type"
            objectType="address"
            :value="applicant.trust.address.building_type"
            :updateValue="updateValue" />
        </div>

        <div class="w-1/3 pr-2">
          <InputText 
            label="Unit number"
            propertyKey="unit_number"
            objectType="address"
            :value="applicant.trust.address.unit_number"
            :updateValue="updateValue" />
        </div>

        <div class="w-1/3">
          <InputText 
            label="Street number"
            propertyKey="street_number"
            objectType="address"
            :value="applicant.trust.address.street_number"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0 flex flex-wrap">
        <div class="w-3/4 pr-2">
          <InputText 
            label="Street name"
            propertyKey="street_name"
            objectType="address"
            :value="applicant.trust.address.street_name"
            :updateValue="updateValue" />

          <div class="mt-1 text-right" v-if="applicant.trust.address.street_name != null && applicant.trust.address.street_name.length > 0">
            <a href="#" class="text-gray-700 hover:underline" @click.prevent="$modal.show('map-modal', { 
              address : getAddressText() 
            })">Show this address on map</a>
          </div>
        </div>

        <div class="w-1/4 pl-1">
          <StreetTypeSelector
            propertyKey="street_type"
            objectType="address"
            :value="applicant.trust.address.street_type"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputText 
          label="Suburb" 
          objectType="address"
          propertyKey="suburb"
          :value="applicant.trust.address.suburb"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputPostcode 
          label="Postcode" 
          objectType="address"
          propertyKey="postcode"
          :value="applicant.trust.address.postcode"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 mt-8 lg:mt-0 pr-0 lg:pr-3">
        <StateSelector
          objectType="address"
          propertyKey="state_id"
          :value="applicant.trust.address.state_id"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <CountrySelector
          objectType="address"
          propertyKey="country_id"
          :value="applicant.trust.address.country_id"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputPhone 
          label="Phone" 
          propertyKey="phone"
          objectType="trust"
          :value="applicant.trust.phone"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputPhone 
          label="Fax" 
          propertyKey="faxPhone"
          objectType="trust"
          :value="applicant.trust.faxPhone"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputEmail 
          label="Email address" 
          propertyKey="email"
          objectType="trust"
          :value="applicant.trust.email"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <CountrySelector
          label="Country where trust was established"
          propertyKey="country_id"
          objectType="trust"
          :value="applicant.trust.country_id"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputText 
          label="Trust business activity/purpose" 
          propertyKey="activity"
          :value="applicant.trust.activity"
          :updateValue="updateValue" />
      </div>
    </div>


    <div class="label-line label-standard">
      If the terms of the Trust identify the beneficiaries by reference to membership of a class, provide details
    </div>

    <div class="form-line">
      <TextareaField 
        label=""
        propertyKey="shortTerm"
        :value="applicant.trust.reference"
        :updateValue="updateValue"/>
    </div>

    <Trustees :trustId="applicant.trust.id"></Trustees>
    <Beneficiaries :trustId="applicant.trust.id"></Beneficiaries>
  </div>
</template>

<script>
  import TitleSelector        from '@/shared/components/form/TitleSelector'
  import StateSelector        from '@/shared/components/form/StateSelector'
  import StreetTypeSelector   from '@/shared/components/form/StreetTypeSelector'
  import BuildingTypeSelector from '@/shared/components/form/BuildingTypeSelector'
  import CountrySelector      from '@/shared/components/form/CountrySelector'
  import InputText            from '@/shared/components/form/InputText'
  import InputName            from '@/shared/components/form/InputName'
  import InputPhone           from '@/shared/components/form/InputPhone'
  import InputPostcode        from '@/shared/components/form/InputPostcode'
  import InputEmail           from '@/shared/components/form/InputEmail'
  import InputABN             from '@/shared/components/form/InputABN'
  import TextareaField        from '@/shared/components/form/TextareaField'
  import Trustees             from './Trustees'
  import Beneficiaries        from './Beneficiaries'
  import { EventBus }         from '@/shared/event-bus';

  export default {

    props : {
      applicant : {
        type     : Object,
        required : true
      }
    },

    components: {
      TitleSelector,
      StateSelector,
      InputText,
      InputName,
      InputPhone,
      InputPostcode,
      InputEmail,
      InputABN,
      CountrySelector,
      TextareaField,
      Beneficiaries,
      Trustees,
      StreetTypeSelector,
      BuildingTypeSelector
    },

    methods : {
      updateTrustName(value) {
        value = value.charAt(0).toUpperCase() + value.slice(1)
        this.$store.commit(this.$store.state.view + "Module/setApplicantName", {
          name       : value,
          applicantId: this.applicant.id
        })
      },

      // a method that will receive the value and update
      updateValue(params) {

        if(params.objectType == 'trust') {
          this.updateTrust(params.key, params.value)
        }

        else if(params.objectType == 'address') {
          this.updateAddress(this.applicant.trust[params.objectType], params.key, params.value)
        }
      },

      updateTrust(key, value) {
        if(this.applicant.trust[key] != value) {
          EventBus.$emit('set-autosave', true)
          this.apiPost({
            clientId   : this.$route.params.clientId,
            applicantId: this.$route.params.applicantId,
            trustId    : this.applicant.trust.id,
            key,
            value
          }, 'applicant/edit-trust', () => {
            EventBus.$emit('set-autosave', false)
          })
        }
      },
    }
  }
</script>